import './Search.scss'

import { HDSButton, HDSHeroSearch } from '@here/hds-react-components'
import classNames from 'classnames'
import List, { DividerProps } from 'components/App/components/List'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { EmptyResults } from 'components/EmptyResults'
import { ActionParamPlaceHolder, Coordinates } from 'data/model'
import { SearchStateListItem, SearchStateListType } from 'data/model/search'
import { RootState } from 'data/reducer'
import {
  clearRecentItemList,
  fetchAutosuggestItemList,
  fetchRecentItemList,
  fetchSearchItemList,
  setAutosuggestItemList,
  setLastUpdatedList,
  setQuery,
  setRecentItem,
  setRecentItemList,
  setSearchItemList,
} from 'data/reducer/search'
import { useAppDispatch, useAppSelector } from 'data/store/hooks'
import { i18n } from 'lib/i18n'
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { calculateAndFormatDistance } from 'utils/formatters'
import { MapSearchParam, parseMapSearchParam } from 'utils/router'
import { RecentItemType, removeRecentItem } from 'utils/storage'

import AutosuggestResultItem, { AutosuggestEvent } from './AutosuggestResultItem'
import SearchHeader from './SearchHeader'
import SearchResultItem from './SearchResultItem'

export enum SearchItemType {
  Autosuggest,
  Search,
}

export interface SearchItemEvent {
  id: string
  type: SearchItemType
}

interface SearchProps {
  highlightItemId?: string
  onItemFocus?: (item: SearchItemEvent) => void
  onItemFocusOut?: (item: SearchItemEvent) => void
  onItemSelect?: (item: SearchItemEvent) => void
  onQuerySelect?: (query: string) => void
  className?: string
}

export default function Search({
  highlightItemId,
  className,
  onItemFocus,
  onItemFocusOut,
  onItemSelect,
  onQuerySelect,
}: SearchProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const dispatch = useAppDispatch()

  const lastUpdatedList = useAppSelector((state: RootState) => state.search.lastUpdatedList)
  const lastUpdatedListRef = useRef<SearchStateListType | undefined>()
  const searchQuery = useAppSelector((state: RootState) => state.search.query)
  const searchQueryRef = useRef<string | undefined>(undefined)
  const autosuggestList = useAppSelector((state: RootState) =>
    lastUpdatedList === SearchStateListType.Autosuggest
      ? state.search.autosuggestList
      : lastUpdatedList === SearchStateListType.Recent
      ? state.search.recentItemList
      : []
  )
  const unit = useAppSelector((state: RootState) => state.app.unit)
  const countryCode = useAppSelector((state: RootState) => state.user.countryCode)
  const searchList = useAppSelector((state: RootState) => state.search.searchItemList)
  const userPosition = useAppSelector((state: RootState) => state.user.currentPosition)
  const isFetching = useAppSelector((state: RootState) => state.search.isLoading)

  const searchParams = useSearchParams()[0]
  const mapSearchParamStr = searchParams.get('map')
  const mapSearchParamStrRef = useRef<string | null>(mapSearchParamStr)
  const mapSearchParam = useMemo(
    () => (mapSearchParamStr !== null ? parseMapSearchParam(mapSearchParamStr) : undefined),
    [mapSearchParamStr]
  )
  const mapSearchParamRef = useRef<MapSearchParam | undefined>(mapSearchParam)
  const { query } = useParams()
  const decodedQuery = useMemo(
    () => (query !== undefined ? decodeURIComponent(query) : undefined),
    [query]
  )
  const isSearchListFetched =
    (decodedQuery !== undefined && isFetching) ||
    (decodedQuery !== undefined && lastUpdatedList === SearchStateListType.Search)

  // This is the way component distinguishes the click action of clear text button 'x'
  const [isClicked, setIsClicked] = useState<boolean>(false)
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const autosuggestDividerOptions: DividerProps = { position: { left: '3rem' } }
  const searchResultsDividerOptions: DividerProps = { position: { left: '0.875rem' } }

  const handleInputChange = useCallback(() => {
    const inputValue = (inputRef.current as HTMLInputElement).value.trim()
    setIsChanged(false)
    setIsClicked(false)
    if (inputValue !== '') {
      const params: [string, Coordinates | undefined] =
        userPosition.coordinates !== null
          ? [
              inputValue,
              {
                lat: userPosition.coordinates.lat,
                lng: userPosition.coordinates.lng,
              },
            ]
          : [inputValue, undefined]
      dispatch(fetchAutosuggestItemList(...params))
    } else {
      dispatch(setLastUpdatedList(SearchStateListType.Recent))
      dispatch(setSearchItemList([]))
      dispatch(setAutosuggestItemList([]))
      dispatch(setQuery(''))
      onQuerySelect !== undefined && onQuerySelect(inputValue)
    }
  }, [dispatch, onQuerySelect, userPosition.coordinates])

  const handleChange = useCallback(() => {
    setIsChanged(true)
    setIsClicked(false)
  }, [setIsClicked, setIsChanged])

  const handleInputFocus = useCallback(() => {
    setIsClicked(false)
    setIsChanged(false)
    const inputValue = (inputRef.current as HTMLInputElement).value.trim()
    if (inputValue === '') {
      dispatch(fetchRecentItemList())
    }
  }, [dispatch])

  // Dispatch search item list in case of query path is the same with the input value.
  const isSameQuery = useCallback(
    (inputQuery: string) => {
      const mapSearchParam = mapSearchParamRef.current

      if (
        mapSearchParam !== undefined &&
        inputQuery !== '' &&
        decodedQuery?.toLowerCase() === inputQuery.toLowerCase()
      ) {
        return true
      }
      return false
    },
    [decodedQuery]
  )

  const handleInputKeyDown = useCallback(
    (e: CustomEvent | KeyboardEvent): void => {
      setIsClicked(false)
      setIsChanged(false)
      // "<HDSHeroSearch />" fires in fact a "KeyboardEvent", but TS declarations define it as "CustomEvent<string>".
      if (!(e instanceof KeyboardEvent)) {
        return
      }

      if (e.key !== 'Enter') {
        return
      }

      const inputValue = (inputRef.current as HTMLInputElement).value.trim()

      if (inputValue === '') {
        return
      }

      dispatch(setRecentItem(undefined, inputValue, RecentItemType.freeText))

      if (mapSearchParamRef.current !== undefined && isSameQuery(inputValue)) {
        dispatch(
          fetchSearchItemList(inputValue, {
            lat: mapSearchParamRef.current.lat,
            lng: mapSearchParamRef.current.lng,
          })
        )
      }
      onQuerySelect !== undefined && onQuerySelect(inputValue)
    },
    [isSameQuery, onQuerySelect, dispatch]
  )

  // custom un-focus callback to hide recent list by clicking outside the input
  const handleDocumentClick = useCallback(
    ({ target }: Event): void => {
      const targetElement = target as HTMLElement

      if (
        !targetElement.classList.contains('SearchResultItem__delete') &&
        document.contains(targetElement) &&
        inputRef.current !== null &&
        !inputRef.current.contains(targetElement) &&
        lastUpdatedList === SearchStateListType.Recent
      ) {
        setIsClicked(false)
        dispatch(setLastUpdatedList(undefined))
      }
    },
    [lastUpdatedList, dispatch]
  )

  const handleAutosuggestResultItemClick = useCallback(
    (event: AutosuggestEvent) => {
      // update input value to the clicked item title
      // dispatch fetchSearchItemList in case you click the same query that you've already searched for
      const item = autosuggestList.find((listItem) => listItem.id === event.id)
      dispatch(setRecentItem(item))
      if (item !== undefined && mapSearchParamRef.current !== undefined && isSameQuery(item.name)) {
        // ;(inputRef.current as HTMLInputElement).value = item.name
        dispatch(
          fetchSearchItemList(item.name, {
            lat: mapSearchParamRef.current.lat,
            lng: mapSearchParamRef.current.lng,
          })
        )
      }
    },
    [autosuggestList, dispatch, isSameQuery]
  )

  const handleAutosuggestResultItemMouseEnter = useCallback(
    (itemId: string): void => {
      onItemFocus !== undefined && onItemFocus({ id: itemId, type: SearchItemType.Autosuggest })
    },
    [onItemFocus]
  )

  const handleAutosuggestResultItemMouseLeave = useCallback(
    (itemId: string): void => {
      onItemFocusOut !== undefined &&
        onItemFocusOut({ id: itemId, type: SearchItemType.Autosuggest })
    },
    [onItemFocusOut]
  )

  const handleSearchResultItemClick = useCallback(
    (itemId: string): void => {
      onItemSelect !== undefined && onItemSelect({ id: itemId, type: SearchItemType.Search })
      const item = searchList.find((listItem) => listItem.id === itemId)
      dispatch(setRecentItem(item))
      if (item !== undefined && mapSearchParamRef.current !== undefined && isSameQuery(item.name)) {
        dispatch(
          fetchSearchItemList(item.name, {
            lat: mapSearchParamRef.current.lat,
            lng: mapSearchParamRef.current.lng,
          })
        )
      }
    },
    [dispatch, isSameQuery, onItemSelect, searchList]
  )

  const handleSearchResultItemMouseEnter = useCallback(
    (itemId: string): void => {
      onItemFocus !== undefined && onItemFocus({ id: itemId, type: SearchItemType.Search })
    },
    [onItemFocus]
  )

  const handleSearchResultItemMouseLeave = useCallback(
    (itemId: string): void => {
      onItemFocusOut !== undefined && onItemFocusOut({ id: itemId, type: SearchItemType.Search })
    },
    [onItemFocusOut]
  )

  const handleCloseButton = useCallback(() => {
    dispatch(setSearchItemList([]))
    dispatch(setAutosuggestItemList([]))
    dispatch(setQuery(''))
    dispatch(setLastUpdatedList(undefined))
    onQuerySelect !== undefined && onQuerySelect('')
  }, [dispatch, onQuerySelect])

  const handleEditButton = useCallback(() => {
    dispatch(setSearchItemList([]))
    dispatch(setLastUpdatedList(SearchStateListType.Autosuggest))
    onQuerySelect !== undefined && onQuerySelect('')
  }, [dispatch, onQuerySelect])

  useEffect(() => {
    return () => {
      dispatch(setSearchItemList([]))
      dispatch(setAutosuggestItemList([]))
      dispatch(setQuery(''))
      dispatch(setLastUpdatedList(undefined))
    }
  }, [dispatch])

  useEffect(() => {
    if (inputRef.current !== document.activeElement) {
      dispatch(setLastUpdatedList(undefined))
    }
  }, [dispatch])

  // That effect triggers the onQuerySelect callback when empty string triggered by clear button.
  // It also cleans up the search state related values, except the recent list which is cached into memory.
  // Furthermore, it keep the focus on input
  useEffect(() => {
    const inputValue = (inputRef?.current as HTMLInputElement)?.value?.trim()

    if (inputValue === '' && isClicked && isChanged) {
      inputRef.current?.focus()
      dispatch(setSearchItemList([]))
      dispatch(setAutosuggestItemList([]))
      dispatch(setQuery(''))
      dispatch(setLastUpdatedList(SearchStateListType.Recent))
      onQuerySelect !== undefined && onQuerySelect(inputValue)
    }
  }, [dispatch, isChanged, isClicked, onQuerySelect])

  // Keeping map search param in a ref, so it can be used for navigation without updating other hooks.
  useEffect(() => {
    mapSearchParamStrRef.current = mapSearchParamStr
    mapSearchParamRef.current = mapSearchParam
  }, [mapSearchParam, mapSearchParamStr])

  // dispatching action to fetch the searchItemList when url query changes,
  // or to set an empty list when query is an empty string
  // if the query is notDefined, it will dispatch the fetchSearchItemList action to cancel any previous identical action dispatched
  useEffect(() => {
    const query = decodedQuery === undefined ? ActionParamPlaceHolder.NotDefined : decodedQuery
    const latLng =
      mapSearchParamRef.current === undefined
        ? ActionParamPlaceHolder.NotDefined
        : { lat: mapSearchParamRef.current.lat, lng: mapSearchParamRef.current.lng }

    dispatch(fetchSearchItemList(query, latLng))
  }, [dispatch, decodedQuery])

  useEffect(() => {
    document.addEventListener('click', handleDocumentClick)
    return () => {
      document.removeEventListener('click', handleDocumentClick)
    }
  }, [handleDocumentClick])

  // Keep the previous values of searchQuery, and lastUpdatedList to create a stateful behavior
  // between search input, and search result visual transition.
  useEffect(() => {
    searchQueryRef.current = searchQuery
    lastUpdatedListRef.current = lastUpdatedList
  }, [decodedQuery, lastUpdatedList, searchQuery])

  // Checking if previous searchQuery, and lastUpdateList are set and updates the input value by keeping the focus in it.
  // Then, it handles the inputChange based on the searchQuery value is set in input
  useEffect(() => {
    if (
      decodedQuery === undefined &&
      searchQueryRef.current !== undefined &&
      lastUpdatedListRef.current === SearchStateListType.Autosuggest
    ) {
      ;(inputRef.current as HTMLInputElement).value = searchQueryRef.current
      inputRef.current?.focus()
      handleInputChange()
    }
  }, [handleInputChange, decodedQuery])

  const handleRecentClearClick = useCallback(
    (e: MouseEvent): void => {
      e.stopPropagation()
      e.preventDefault()
      dispatch(clearRecentItemList('landing'))
    },
    [dispatch]
  )

  const handleDeleteRecentItem = useCallback(
    (e: MouseEvent, id: string): void => {
      e.stopPropagation()
      e.preventDefault()
      removeRecentItem(id)

      const newRecents = [...autosuggestList]
      const index = newRecents.findIndex((item) => item.id === id)
      newRecents.splice(index, 1)
      dispatch(setRecentItemList(newRecents))
    },
    [autosuggestList, dispatch]
  )

  return (
    <div className={className}>
      {isSearchListFetched ? (
        <SearchHeader title={decodedQuery} onClose={handleCloseButton} onEdit={handleEditButton} />
      ) : (
        <div className={classNames('Search__input-container')}>
          <HDSHeroSearch
            ref={inputRef}
            placeholder={i18n('search.input-placeholder')}
            onChange={handleChange}
            onInput={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onFocus={handleInputFocus}
            onBlur={() => setIsClicked(false)}
            onClick={() => setIsClicked(true)}
          />
        </div>
      )}
      <ScrollableContainer className="SearchResultList-scrollable-container">
        <>
          {(lastUpdatedList === SearchStateListType.Autosuggest ||
            lastUpdatedList === SearchStateListType.Recent) && (
            <>
              <List className="SearchResultList" divider={autosuggestDividerOptions}>
                {autosuggestList.map((item: SearchStateListItem, index) => {
                  const link =
                    mapSearchParamStr === null
                      ? item.urlPath
                      : `${item.urlPath}?map=${mapSearchParamStr}`
                  return (
                    <AutosuggestResultItem
                      key={`${index}-${item.id}`}
                      id={item.id}
                      link={link}
                      kind={lastUpdatedList}
                      title={item.title}
                      categoryId={item.categoryId}
                      highlightedSubtitle={
                        item.position && userPosition.coordinates !== null
                          ? calculateAndFormatDistance(item.position, userPosition.coordinates, {
                              countryCode,
                              unit,
                            })
                          : item.distanceLabel
                      }
                      iconName={item.iconName}
                      subtitle={item.subtitle}
                      onClick={handleAutosuggestResultItemClick}
                      onMouseEnter={handleAutosuggestResultItemMouseEnter}
                      onMouseLeave={handleAutosuggestResultItemMouseLeave}
                      onDeleteItem={handleDeleteRecentItem}
                    />
                  )
                })}
              </List>
              {lastUpdatedList === SearchStateListType.Recent &&
                autosuggestList.length > 0 &&
                false && (
                  <div className="RoutePlanner__clear-all-container">
                    <HDSButton
                      size="small"
                      variant="subtle-quiet"
                      onMouseDown={handleRecentClearClick}
                    >
                      {i18n('search.recent.clear')}
                    </HDSButton>
                  </div>
                )}
            </>
          )}
          {lastUpdatedList === SearchStateListType.Search && (
            <List className="SearchResultList" divider={searchResultsDividerOptions}>
              {searchList.map((item: SearchStateListItem, index) => {
                const link =
                  mapSearchParamStr === null
                    ? item.urlPath
                    : `${item.urlPath}?map=${mapSearchParamStr}`
                return (
                  <SearchResultItem
                    id={item.id}
                    key={`${index}-${item.id}`}
                    iconName={item.iconName}
                    isHighlighted={highlightItemId === item.id}
                    categoryId={item.categoryId}
                    title={item.title}
                    link={link}
                    thumbnail={item.thumbnail}
                    averageRating={item.averageRating}
                    reviewCount={item.reviewCount}
                    highlightedSubtitle={item.distanceLabel}
                    subtitle={item.subtitle}
                    onClick={handleSearchResultItemClick}
                    onMouseEnter={handleSearchResultItemMouseEnter}
                    onMouseLeave={handleSearchResultItemMouseLeave}
                  />
                )
              })}
            </List>
          )}
          {lastUpdatedList === SearchStateListType.Search && searchList.length === 0 && (
            <EmptyResults
              className="Search__empty-results"
              title={i18n(`no-results-card.have-another-go`)}
              message={i18n(`no-results-card.message-label-search`)}
            />
          )}
        </>
      </ScrollableContainer>
    </div>
  )
}
